import { Link } from 'react-router-dom'
import classes from './ChatWidget.module.scss'
import { ChatCompanyDebt, ChatCurrency, ChatDebtStatus } from './types'
import { formatChatAmount, getChatCurrencyLabel } from './chatFormatters'
import { getProductCompanyBadge } from './productCompany'

const PAYMENT_PATH = '/myspecial/mypayments'

/** Sütun sırası sabit: satırlar arasında aynı döviz aynı kolona düşsün. */
const CURRENCY_ORDER: ChatCurrency[] = ['TRY', 'USD', 'EUR']

/** Backend şirket adı yerine kodu tekrarlıyor ("Şirket 1000") — bu ad kullanılmaz, marka adına çevrilir. */
const PLACEHOLDER_COMPANY_NAME = /^şirket\s*\d+$/i

/** Marka adı/logosu şirket kodundan çözülür; backend anlamlı bir ad gönderdiyse o kullanılır. */
function resolveCompany(company: ChatCompanyDebt) {
  const badge = getProductCompanyBadge(Number(company.companyCode))
  const rawName = company.companyName?.trim()
  const isMeaningful = !!rawName && !PLACEHOLDER_COMPANY_NAME.test(rawName)

  return {
    name: (isMeaningful ? rawName : badge?.name || rawName) || `Şirket ${company.companyCode}`,
    logoSrc: badge?.logoSrc ?? null,
  }
}

/**
 * Footer toplamları döviz başına satırlardan toplanır; backend tek para birimlik `Total`
 * gönderdiği için o para birimi backend değeriyle ezilir (kaynak veri esas alınsın).
 */
function sumTotals(debtStatus: ChatDebtStatus): Partial<Record<ChatCurrency, number>> {
  const totals: Partial<Record<ChatCurrency, number>> = {}

  for (const company of debtStatus.companies) {
    for (const money of company.amounts) {
      totals[money.currency] = (totals[money.currency] ?? 0) + money.amount
    }
  }
  if (debtStatus.total) totals[debtStatus.total.currency] = debtStatus.total.amount

  return totals
}

/**
 * "Güncel Borç/Alacak Durumunuz" kartı: şirket başına borç satırı + "Öde" butonu ve
 * altta kırmızı TOPLAM BORÇ şeridi. Tutarlar tek cümle değil, hem satırlarda hem
 * footer'da döviz başına sütun olarak gösterilir; "Öde" butonu mevcut ödeme
 * sayfasına (/myspecial/mypayments) yönlendirir.
 */
export default function DebtStatusCard({ debtStatus }: { debtStatus: ChatDebtStatus }) {
  if (debtStatus.companies.length === 0) return null

  // Yalnız veride geçen dövizler sütuna dönüşür; hiç EUR yoksa boş kolon açılmaz.
  const currencies = CURRENCY_ORDER.filter(currency =>
    debtStatus.companies.some(company => company.amounts.some(money => money.currency === currency))
  )
  const gridStyle = { gridTemplateColumns: `repeat(${Math.max(currencies.length, 1)}, minmax(0, 1fr))` }
  const totals = sumTotals(debtStatus)

  return (
    <div className={classes.debtCard}>
      {debtStatus.companies.map((company, i) => {
        const { name, logoSrc } = resolveCompany(company)

        return (
          <div key={`${company.companyCode}-${i}`} className={classes.debtRow}>
            <div className={classes.debtRowInfo}>
              <div className={classes.debtCompany}>
                {logoSrc && <img className={classes.debtCompanyLogo} src={logoSrc} alt='' />}
                <span>{name}</span>
              </div>
              <div className={classes.debtAmountGrid} style={gridStyle}>
                {currencies.map(currency => {
                  const money = company.amounts.find(item => item.currency === currency)

                  return (
                    <div key={currency} className={classes.debtAmountCell}>
                      <span className={classes.debtAmountCurrency}>{getChatCurrencyLabel(currency)}</span>
                      <span className={money ? classes.debtAmountValue : classes.debtAmountEmpty}>
                        {money ? formatChatAmount(money.amount) : '—'}
                      </span>
                    </div>
                  )
                })}
              </div>
              {company.detail && <div className={classes.debtDetail}>{company.detail}</div>}
            </div>
            <Link className={classes.debtPayBtn} to={PAYMENT_PATH} target='_blank' rel='noopener noreferrer'>
              Öde
            </Link>
          </div>
        )
      })}

      {debtStatus.total && (
        <div className={classes.debtTotal}>
          <div className={classes.debtTotalLabel}>{debtStatus.totalLabel?.trim() || 'Toplam Borç'}</div>
          <div className={classes.debtTotalGrid} style={gridStyle}>
            {currencies.map(currency => {
              const amount = totals[currency]

              return (
                <div key={currency} className={classes.debtTotalCell}>
                  <span className={classes.debtTotalCurrency}>{getChatCurrencyLabel(currency)}</span>
                  <span className={amount === undefined ? classes.debtTotalEmpty : classes.debtTotalAmount}>
                    {amount === undefined ? '—' : formatChatAmount(amount)}
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
